import React, { useCallback } from 'react';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { useAtAGlanceData } from '../store/atAGlanceBridge.ts';
import { type AskRovoTrigger } from '../askRovoAnalytics.ts';

import { useConnectorPromoBanner } from './promo-banner/hooks/useConnectorPromoBanner.tsx';
import { ConnectionSections } from './sections/ConnectionSections.tsx';
import type { ConnectionSectionsData } from './sections/useConnectionSectionsData.ts';
import { useLinksContextPrompt } from './sections/useLinksContextPrompt.ts';
import { VisualizationWrapper } from './visualization/VisualizationWrapper.tsx';

export type TeamworkGraphPreviewPanelContextColumnProps = {
	contentId?: string;
	connections: ConnectionSectionsData;
	isRovoEnabled: boolean;
	/**
	 * Switches the panel's body over to Rovo chat. This column binds each entry's `trigger`
	 * before handing it on — the row actions and the "At a glance" button — so the sections stay
	 * trigger-agnostic. Opens the chat unseeded unless a prompt is passed.
	 */
	onAskRovo: (trigger: AskRovoTrigger, prompt?: string) => void;
	/** Owned by the panel so the header button can open the modal that this column mounts. */
	isModalOpen: boolean;
	onCloseModal: () => void;
};

/**
 * The preview panel's body as it opens: the connection sections, the connector promo banner,
 * and the lazy full-size graph modal.
 *
 * The description is deliberately not here — it lives behind the header's
 * `TeamworkGraphPreviewPanelInfoButton` tooltip. The modal shows no inline description either.
 *
 * The panel's counterpart to `TeamworkGraphContextColumn`, and like that one it renders the
 * connections it is handed rather than fetching them, so the surface keeps to a single fetch.
 *
 * At a glance is read from the byline's bridge. Only the promo banner is resolved
 * here, so the panel and modal share one banner and its viewed event.
 *
 * It owns the spacing between its own rows and nothing else — padding and scrolling belong to
 * `PreviewPanelBody`, which is also what the Rovo chat replaces this column inside.
 */
export function TeamworkGraphPreviewPanelContextColumn({
	contentId,
	connections,
	isRovoEnabled,
	onAskRovo,
	isModalOpen,
	onCloseModal,
}: TeamworkGraphPreviewPanelContextColumnProps): React.JSX.Element {
	const { atAGlance } = useAtAGlanceData(contentId);
	const { element: promoBanner } = useConnectorPromoBanner({ links: connections.linksTo.links });

	// Bound here rather than at the row, so the switch/close analytics can name the control
	// without every row action carrying the trigger. Wrapped rather than passed straight through,
	// or React would forward the click event as the prompt.
	const onAskRovoFromRow = useCallback(
		(prompt?: string) => onAskRovo('rowAction', prompt),
		[onAskRovo],
	);
	// The panel's "At a glance" entry, seeded with every connection url — the same prompt the
	// modal's "Add links to Rovo context" button carries — so the chat opens with the links as
	// context. Null when there are no links to send, in which case it opens unseeded.
	// Wrapped so the click event never becomes the prompt.
	const atAGlanceLinksPrompt = useLinksContextPrompt(connections);
	const onAskRovoFromAtAGlance = useCallback(
		() => onAskRovo('atAGlance', atAGlanceLinksPrompt ?? undefined),
		[onAskRovo, atAGlanceLinksPrompt],
	);

	return (
		<>
			<VisualizationWrapper
				isOpen={isModalOpen}
				onClose={onCloseModal}
				contentId={contentId}
				connections={connections}
				atAGlance={atAGlance}
				promoBanner={promoBanner}
			/>
			<Stack space="space.300">
				<ConnectionSections
					connections={connections}
					atAGlance={atAGlance}
					onAskRovo={isRovoEnabled ? onAskRovoFromRow : undefined}
					onAtAGlanceAskRovo={isRovoEnabled ? onAskRovoFromAtAGlance : undefined}
				/>
				{promoBanner}
			</Stack>
		</>
	);
}
